/**
 * shared/gift-ideas.ts — the PURE idea composer for the phase-2 gift flow
 * (card NYJEJRCBfBGQ). No network, no model call, no key: with apiserpent
 * credits at 0 there are no live listings to merge in, so the flow's step 3
 * is filled from the neutral fixture (shared/fixtures/gift-ideas.ts) and
 * every rec is written with source: 'idea' — the row itself forbids a url on
 * an idea, and the UI labels it "Idea, AI-suggested.", never "Live listing".
 *
 * THE WHY RULE: a rationale may cite ONLY (a) the recipient's SHARED interest
 * topics (the partner-visible half of the interests profile — answers are
 * never read) and (b) what the giver typed into the brief. Private lists
 * (has_owned / not_taste rows) are never an input here at all, so no Why:
 * line can quote or paraphrase one.
 */
import { formatPrice } from './apiserpent.ts';
import { GIFT_IDEA_TEMPLATES, type GiftIdeaTemplate } from './fixtures/gift-ideas.ts';

export interface GiftIdeaInput {
  kind: string | null;
  vibe: string | null;
  budgetMin: number | null;
  budgetMax: number | null;
  /** What the giver typed — quotable in a Why: line. */
  freeText: string;
  /** Avoid items already written into the brief (e.g. "not this" reasons). */
  avoid: string[];
  /** The RECIPIENT's shared interest topics — the only profile half allowed. */
  topics: string[];
  /** Display name for the Why line ("Nina"). */
  partnerName: string;
  /** YYYY-MM-DD the giver wants it in hand by, or null. */
  deadline: string | null;
  /** Deterministic rotation so a regenerate can differ without randomness. */
  seed: number;
  count?: number;
}

export interface GiftIdea {
  source: 'idea';
  title: string;
  /** 'A$85' — an estimate; the card renders it with a ~. */
  priceEstimate: string;
  rationale: string;
  deadline: string | null;
}

/**
 * THE QUALITY FLOOR (card t23OZwdtxs4k, spec §7).
 *
 * A candidate is worth showing only if it carries at least one STRONG signal
 * about this gift: the kind matches, the vibe matches, the price sits inside
 * the budget, or it touches one of the recipient's shared interests. A
 * candidate with none of those is a guess wearing a card — it is dropped, and
 * the set is filled from the ideas that did clear the floor. If nothing
 * clears it, the flow says so and asks one follow-up question instead of
 * padding three weak cards to look busy.
 *
 * The weight each signal carries and the floor itself are named here on
 * purpose: the founder still owns the threshold value, so settling it is a
 * one-number change, not a code change.
 */
export const SIGNAL_WEIGHTS = { kind: 3, vibe: 2, budgetMax: 2, budgetMin: 1, topic: 1 } as const;
/** Minimum weight a candidate must reach to be shown at all. */
export const IDEA_FLOOR = 1;
/** How many cards one Ideas step shows when the floor can fill them. */
export const IDEA_SET_SIZE = 3;

export interface GiftIdeaSignals {
  kind: boolean;
  vibe: boolean;
  budget: boolean;
  topic: number;
  total: number;
}

export interface GiftIdeaSet {
  /** Cards to show, at most IDEA_SET_SIZE, every one above the floor. */
  ideas: GiftIdea[];
  /** How many candidates cleared the floor (0..IDEA_SET_SIZE is the useful range). */
  qualified: number;
  /** True when nothing cleared the floor — show the no-fit copy, not an empty list. */
  noFit: boolean;
  /** The single follow-up question when noFit, else null. */
  followUp: string | null;
}

function hashSeed(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

function mentions(haystack: string, needle: string): boolean {
  const h = haystack.toLowerCase();
  const n = needle.trim().toLowerCase();
  return n.length > 0 && h.includes(n);
}

/** Does this idea collide with an avoid item (title or tags)? */
function isAvoided(t: GiftIdeaTemplate, avoid: string[]): boolean {
  const hay = `${t.title} ${t.tags.join(' ')}`;
  return avoid.some((a) => mentions(hay, a));
}

/** Break the score into named signals, so the floor and the probe speak the
 *  same language. `budget` is true when the price sits inside the budget. */
export function signalsFor(t: GiftIdeaTemplate, input: GiftIdeaInput, topicHits: string[]): GiftIdeaSignals {
  const kind = Boolean(input.kind && t.kind === input.kind);
  const vibe = Boolean(input.vibe && t.vibe === input.vibe);
  const withinMax = input.budgetMax !== null && t.priceAud <= input.budgetMax;
  const aboveMin = input.budgetMin !== null && t.priceAud >= input.budgetMin;
  const total =
    (kind ? SIGNAL_WEIGHTS.kind : 0) +
    (vibe ? SIGNAL_WEIGHTS.vibe : 0) +
    (withinMax ? SIGNAL_WEIGHTS.budgetMax : 0) +
    (aboveMin ? SIGNAL_WEIGHTS.budgetMin : 0) +
    topicHits.length * SIGNAL_WEIGHTS.topic;
  return { kind, vibe, budget: withinMax || aboveMin, topic: topicHits.length, total };
}

/** Build one Why: line from shared topics + the giver's own words only. */
function rationaleFor(t: GiftIdeaTemplate, input: GiftIdeaInput, topicHits: string[]): string {
  const bits: string[] = [];
  if (topicHits.length > 0) {
    const names = topicHits.slice(0, 2).map((x) => `“${x}”`).join(' and ');
    bits.push(`${input.partnerName}’s shared interest in ${names}`);
  }
  const note = input.freeText.trim().replace(/\s+/g, ' ').slice(0, 80);
  if (note) bits.push(`your note “${note}${input.freeText.trim().length > 80 ? '…' : ''}”`);
  if (bits.length === 0) {
    return 'Why: an AI-suggested idea from your brief — no shared interests on file yet.';
  }
  return `Why: fits ${bits.join(' and ')}.`;
}

/**
 * The one follow-up question when nothing clears the floor. One question,
 * about the recipient, answerable in a sentence — never a form and never an
 * apology dressed as a suggestion list.
 */
export function noFitFollowUp(partnerName: string): string {
  return `Nothing fits that brief yet. What does ${partnerName} actually spend their time on?`;
}

export function composeGiftIdeaSet(input: GiftIdeaInput): GiftIdeaSet {
  const count = input.count ?? IDEA_SET_SIZE;
  const seedText = `${input.freeText}|${input.avoid.join(',')}|${input.kind ?? ''}|${input.vibe ?? ''}`;
  const rotation = hashSeed(seedText) % Math.max(GIFT_IDEA_TEMPLATES.length, 1);
  const ranked = GIFT_IDEA_TEMPLATES
    .filter((t) => !isAvoided(t, input.avoid))
    .map((t) => {
      const topicHits = input.topics.filter((topic) => mentions(`${t.title} ${t.tags.join(' ')}`, topic) || mentions(topic, t.title));
      const signals = signalsFor(t, input, topicHits);
      return { t, topicHits, s: signals.total };
    })
    // THE FLOOR: a candidate with no strong signal is not shown, however much
    // of the list it would pad out (spec §7, card t23OZwdtxs4k).
    .filter(({ s }) => s >= IDEA_FLOOR)
    .sort((a, b) => b.s - a.s || a.t.id.localeCompare(b.t.id));
  // Rotate the qualified list by the seed so a regenerate (new brief version /
  // new avoid item) can surface different ideas, deterministically.
  const rotated = ranked.length > 0
    ? ranked.slice(rotation % ranked.length).concat(ranked.slice(0, rotation % ranked.length))
    : ranked;
  const ideas = rotated.slice(0, count).map(({ t, topicHits }) => ({
    source: 'idea' as const,
    title: t.title,
    priceEstimate: formatPrice(t.priceAud, 'AUD') ?? `A$${t.priceAud}`,
    rationale: rationaleFor(t, input, topicHits),
    deadline: input.deadline,
  }));
  return {
    ideas,
    qualified: ranked.length,
    noFit: ideas.length === 0,
    followUp: ideas.length === 0 ? noFitFollowUp(input.partnerName) : null,
  };
}

/** The ideas alone — the shape phase 2 shipped with. Prefer the set: it also
 *  carries the shortfall and the follow-up a no-fit flow needs. */
export function composeGiftIdeas(input: GiftIdeaInput): GiftIdea[] {
  return composeGiftIdeaSet(input).ideas;
}
